import { FC } from 'react';
import { OpenAPIV3_1 as OpenApi } from 'openapi-types';
import { Box, Input, Checkbox, Select } from '@robocorp/components';
import { styled } from '@robocorp/theme';
import { useTranslation } from 'react-i18next';
import { getRef } from '../utils/payload';

const Container = styled(Box)`
  label span {
    text-transform: initial;

    &::first-letter {
      text-transform: capitalize;
    }
  }
`;

type Props = {
  value: string | number | boolean | undefined;
  parameter: OpenApi.ParameterObject;
  onParameterChange: (key: string, parameter: string | number | boolean | undefined) => void;
  components: OpenApi.ComponentsObject['schemas'];
};

export const ApiHelperParameter: FC<Props> = ({
  value,
  parameter,
  onParameterChange,
  components,
}) => {
  const { t } = useTranslation();

  const partialSchema =
    components && parameter.schema && '$ref' in parameter.schema
      ? getRef(parameter.schema.$ref, components).value
      : parameter.schema;

  const schema = partialSchema && 'type' in partialSchema ? partialSchema : {};

  const parameterName = parameter.name.replaceAll('_', ' ');

  if (schema.type === 'boolean') {
    return (
      <Container mb={10} key={parameter.name}>
        <Checkbox
          label={parameterName}
          checked={(value as boolean) ?? false}
          onChange={(event) => onParameterChange(parameter.name, event.target.value)}
        />
      </Container>
    );
  }

  if (schema.type === 'string' && !schema.enum) {
    return (
      <Container mb={20} key={parameter.name}>
        <Input
          label={parameterName}
          value={(value as string) ?? ''}
          onChange={(event) => onParameterChange(parameter.name, event.target.value)}
        />
      </Container>
    );
  }

  if (schema.type === 'string' && schema.enum) {
    const listOptions = schema.enum.map((item) => ({ label: item, value: item }));
    return (
      <Container mb={20} key={parameter.name}>
        <Select
          label={parameterName}
          value={(value as string) ?? ''}
          items={
            parameter.required ? listOptions : [{ label: t('none'), value: '' }, ...listOptions]
          }
          onChange={(value) => onParameterChange(parameter.name, value)}
          placeholder={t('none')}
        />
      </Container>
    );
  }

  if (schema.type === 'number') {
    return (
      <Container mb={20} key={parameter.name}>
        <Input
          label={parameterName}
          value={(value as string) ?? ''}
          onChange={(event) => onParameterChange(parameter.name, Number(event.target.value) || '')}
          min={0}
        />
      </Container>
    );
  }

  return null;
};
